import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  TextField,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import classNames from 'classnames';
import { without } from 'ramda';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDB } from 'react-pouchdb';
import DeleteIcon from '@mui/icons-material/Delete';
import normalizeText from '../../../../helpers/normalizeText';
import { ProductPouchEntity, TransactionItem as TransactionItemType } from '../../../../model';
import { Theme } from '../../../../theme';
import { setDisabledKeydown } from '../../../store/scanner';
import { validateEan } from '../../eanValidation';

const useStyles = makeStyles(({ spacing, palette }: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  formField: {
    margin: spacing(2, 0, 1, 0),
  },
  toBeDeleted: {
    color: palette.error.main,
  },
  protectedCode: {
    opacity: 0.5,
    fontStyle: 'italic',
  },
}));

type Props = StandardProps & {
  onClose?: () => void;
  onConfirm: (newEan: string, eansToDelete: string[]) => void;
  onCancel?: () => void;
  product: TransactionItemType;
};

const TransactionEanDialog: React.FC<Props> = ({ onConfirm, onCancel = () => {}, onClose, product, ...others }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [newEanValue, setNewEanValue] = useState('');
  const [eansToDelete, setEansToDelete] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const db: PouchDB.Database = useDB();

  // Product CODE should not be deletable
  const productCode = product.product.code;
  const eans = useMemo(() => product.product.ean ?? [], [product.product.ean]);

  const markForDelete = useCallback(
    (eanValue: string) => {
      // Prevent deleting product CODE
      if (eanValue === productCode) {
        return;
      }

      if (eansToDelete.includes(eanValue)) {
        setEansToDelete(without([eanValue], eansToDelete));
      } else {
        setEansToDelete([...eansToDelete, eanValue]);
      }
    },
    [eansToDelete, productCode]
  );

  const handleCancel = useCallback(() => {
    dispatch(setDisabledKeydown(false));
    onCancel();
  }, [onCancel, dispatch]);

  // Unified validation: format + duplicate within product + cross-product uniqueness
  const validateEanValue = useCallback(async () => {
    const trimmedValue = newEanValue.trim();

    // Empty value is valid (no EAN to add)
    if (!trimmedValue) {
      setValidationError(null);
      setIsValidating(false);
      return;
    }

    setIsValidating(true);

    // Step 1: Format validation
    const formatResult = validateEan(trimmedValue);
    if (!formatResult.valid) {
      setValidationError(formatResult.errors.join(', '));
      setIsValidating(false);
      return;
    }

    // Step 2: Check duplicate within current product (not marked for deletion)
    const existingEans = eans.filter((ean) => !eansToDelete.includes(ean));
    if (existingEans.includes(trimmedValue)) {
      setValidationError('EAN již existuje v tomto produktu');
      setIsValidating(false);
      return;
    }

    // Step 3: Check uniqueness across all products in database
    try {
      const response = await db.query<ProductPouchEntity>('views/ean_code_view', {
        key: normalizeText(trimmedValue),
        include_docs: true,
      });

      if (response.rows.length > 0) {
        const existingProduct = response.rows[0].doc;
        if (existingProduct && existingProduct.productId !== product.product.productId) {
          setValidationError(`EAN již existuje u produktu: ${existingProduct.name} (${existingProduct.code})`);
          setIsValidating(false);
          return;
        }
      }

      // All validations passed
      setValidationError(null);
    } catch (error) {
      console.error('Error checking EAN uniqueness:', error);
      setValidationError('Chyba při kontrole jedinečnosti EAN');
    } finally {
      setIsValidating(false);
    }
  }, [newEanValue, eans, eansToDelete, db, product.product.productId]);

  // Debounce validation on input change - only depend on the input value
  useEffect(() => {
    const timeoutId = setTimeout(validateEanValue, 500);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newEanValue]);

  const hasValidationError = touched && !!validationError;
  const canSave = !newEanValue.trim() || (!validationError && !isValidating);

  const handleSave = useCallback(() => {
    if (!canSave) {
      return;
    }
    dispatch(setDisabledKeydown(false));
    onConfirm(newEanValue, eansToDelete);
  }, [dispatch, onConfirm, newEanValue, eansToDelete, canSave]);

  useEffect(() => {
    dispatch(setDisabledKeydown(true));
    return () => {
      dispatch(setDisabledKeydown(false));
    };
  }, [dispatch]);

  return (
    <Dialog open fullScreen {...others} className={classNames(classes.root)} data-test="eanModal">
      <DialogTitle>{product.product.name} - Upravit EAN</DialogTitle>
      <IconButton onClick={handleCancel} className={classes.closeButton} size="large">
        <Close />
      </IconButton>
      <DialogContent>
        <Box sx={{ flex: '1 1 40%', minWidth: 0, '&>*': { overflowWrap: 'break-word' } }} color={'text.primary'}>
          <Typography variant={'h4'}>{product.product.name}</Typography>
          <Typography variant={'body1'}>Kód: {product.product.code}</Typography>
        </Box>
        <Divider />
        <TextField
          autoFocus
          variant="standard"
          margin="dense"
          id="newEan"
          label={'Zadejte nový EAN'}
          type="text"
          fullWidth
          value={newEanValue}
          onChange={(e) => {
            setNewEanValue(e.target.value);
            if (!touched) setTouched(true);
          }}
          onBlur={() => setTouched(true)}
          error={hasValidationError}
          className={classes.formField}
          helperText={hasValidationError ? validationError : 'Minimálně 3 znaky, pouze písmena a čísla'}
        />

        <Typography
          variant="caption"
          color="error"
          sx={{ mb: 1, display: 'block', visibility: isValidating ? 'visible' : 'hidden' }}
        >
          Kontrolujem jedinečnost EAN...
        </Typography>

        <DialogContentText color={'text.primary'} component={'div'}>
          <Typography variant={'h4'}>Existující EAN kódy</Typography>
          {eans.length === 0 ? (
            <Typography variant={'body2'} color={'text.secondary'}>
              Žádné EAN kódy
            </Typography>
          ) : (
            <List>
              {eans.map((item) => {
                const isProductCode = item === productCode;
                const isMarkedForDelete = eansToDelete.includes(item);

                return (
                  <ListItem
                    key={item}
                    className={classNames({
                      [classes.toBeDeleted]: isMarkedForDelete,
                      [classes.protectedCode]: isProductCode,
                    })}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => markForDelete(item)}
                        disabled={isProductCode}
                      >
                        <DeleteIcon color={isMarkedForDelete ? 'error' : isProductCode ? 'disabled' : 'inherit'} />
                      </IconButton>
                    }
                  >
                    {item}
                    {isProductCode && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (kód produktu)
                      </Typography>
                    )}
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="secondary" fullWidth variant="contained" disabled={isValidating}>
          Zrušit
        </Button>
        <Button onClick={handleSave} color="primary" fullWidth variant="contained" disabled={!canSave || isValidating}>
          Uložit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionEanDialog;
